import React, { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import {
  CurrencyDollarIcon,
  UserIcon,
  ClockIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  XMarkIcon,
  MagnifyingGlassIcon,
  FunnelIcon,
  BanknotesIcon,
  ReceiptRefundIcon,
} from '@heroicons/react/24/outline'
import { Button } from '@/components/ui/Button'
import { useAuth } from '@/hooks/useAuth'
import {
  getFines,
  payFine,
  waiveFine,
  createFine,
  type Fine,
  type FineStatus,
  type FineType,
} from '@/api/libraryApi'
import { getUsers, type User } from '@/api/usersApi'
import toast from 'react-hot-toast'

const FinesPage: React.FC = () => {
  const { user } = useAuth()
  const [fines, setFines] = useState<Fine[]>([])
  const [totals, setTotals] = useState<any>({})
  const [loading, setLoading] = useState(true)
  const [statusFilter, setStatusFilter] = useState<string>('pending')
  const [typeFilter, setTypeFilter] = useState<string>('all')
  const [showPayModal, setShowPayModal] = useState(false)
  const [showWaiveModal, setShowWaiveModal] = useState(false)
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [selectedFine, setSelectedFine] = useState<Fine | null>(null)
  const [currentPage, setCurrentPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const pageSize = 20

  // Payment form state
  const [paymentAmount, setPaymentAmount] = useState('')
  const [paymentMethod, setPaymentMethod] = useState('cash')
  const [receiptNumber, setReceiptNumber] = useState('')
  const [paymentNotes, setPaymentNotes] = useState('')

  // Waive form state
  const [waiveReason, setWaiveReason] = useState('')

  // Create fine form state
  const [newFineUserId, setNewFineUserId] = useState('')
  const [newFineType, setNewFineType] = useState<FineType>('other')
  const [newFineAmount, setNewFineAmount] = useState('')
  const [newFineReason, setNewFineReason] = useState('')
  const [userSearch, setUserSearch] = useState('')
  const [userResults, setUserResults] = useState<User[]>([])
  const [selectedUser, setSelectedUser] = useState<User | null>(null)

  useEffect(() => {
    fetchFines()
  }, [currentPage, statusFilter, typeFilter])

  const fetchFines = async () => {
    try {
      setLoading(true)
      const response = await getFines({
        page: currentPage,
        limit: pageSize,
        status: statusFilter !== 'all' ? (statusFilter as FineStatus) : undefined,
        fineType: typeFilter !== 'all' ? (typeFilter as FineType) : undefined,
      })
      setFines(response.data)
      setTotals(response.totals || {})
      setTotalPages(response.pagination.pages)
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to load fines')
    } finally {
      setLoading(false)
    }
  }

  const searchUsers = async (query: string) => {
    if (query.length < 2) {
      setUserResults([])
      return
    }
    try {
      const response = await getUsers({ search: query, limit: 10 })
      setUserResults(response.data)
    } catch (error) {
      console.error('Failed to search users:', error)
    }
  }

  const handlePayFine = async () => {
    if (!selectedFine || !paymentAmount) return

    try {
      await payFine(selectedFine._id, {
        amount: parseFloat(paymentAmount),
        paymentMethod,
        receiptNumber: receiptNumber || undefined,
        notes: paymentNotes || undefined,
      })
      toast.success('Payment recorded successfully')
      setShowPayModal(false)
      resetPaymentForm()
      fetchFines()
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to record payment')
    }
  }

  const handleWaiveFine = async () => {
    if (!selectedFine || !waiveReason) return

    try {
      await waiveFine(selectedFine._id, waiveReason)
      toast.success('Fine waived successfully')
      setShowWaiveModal(false)
      setWaiveReason('')
      setSelectedFine(null)
      fetchFines()
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to waive fine')
    }
  }

  const handleCreateFine = async () => {
    if (!selectedUser || !newFineAmount || !newFineReason) return

    try {
      await createFine({
        userId: selectedUser._id,
        fineType: newFineType,
        amount: parseFloat(newFineAmount),
        reason: newFineReason,
      })
      toast.success('Fine created successfully')
      setShowCreateModal(false)
      resetCreateForm()
      fetchFines()
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to create fine')
    }
  }

  const resetPaymentForm = () => {
    setPaymentAmount('')
    setPaymentMethod('cash')
    setReceiptNumber('')
    setPaymentNotes('')
    setSelectedFine(null)
  }

  const resetCreateForm = () => {
    setNewFineUserId('')
    setNewFineType('other')
    setNewFineAmount('')
    setNewFineReason('')
    setUserSearch('')
    setUserResults([])
    setSelectedUser(null)
  }

  const getStatusColor = (status: FineStatus) => {
    switch (status) {
      case 'pending':
        return 'bg-amber-100 text-amber-700'
      case 'partial':
        return 'bg-blue-100 text-blue-700'
      case 'paid':
        return 'bg-green-100 text-green-700'
      case 'waived':
        return 'bg-purple-100 text-purple-700'
      case 'written_off':
        return 'bg-gray-100 text-gray-700'
      default:
        return 'bg-gray-100 text-gray-700'
    }
  }

  const getFineTypeLabel = (type: FineType) => {
    switch (type) {
      case 'late_return':
        return 'Late Return'
      case 'lost_book':
        return 'Lost Book'
      case 'damaged_book':
        return 'Damaged Book'
      case 'lost_card':
        return 'Lost Card'
      case 'other':
        return 'Other'
      default:
        return type
    }
  }

  const pendingTotal = totals.pending?.total || 0
  const partialTotal = totals.partial?.total || 0
  const collectedTotal = totals.paid?.paid || 0

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Fines Management</h1>
          <p className="text-gray-500 mt-1">Manage library fines and payments</p>
        </div>
        <Button onClick={() => setShowCreateModal(true)} className="flex items-center gap-2">
          <CurrencyDollarIcon className="w-5 h-5" />
          Create Fine
        </Button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-gradient-to-br from-amber-500 to-amber-600 rounded-xl p-6 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-amber-100 text-sm">Pending Fines</p>
              <p className="text-3xl font-bold mt-1">
                KES {(pendingTotal + partialTotal).toLocaleString()}
              </p>
            </div>
            <ExclamationTriangleIcon className="w-12 h-12 text-amber-200" />
          </div>
          <p className="text-amber-100 text-sm mt-2">
            {(totals.pending?.count || 0) + (totals.partial?.count || 0)} outstanding
          </p>
        </div>

        <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-xl p-6 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-green-100 text-sm">Collected</p>
              <p className="text-3xl font-bold mt-1">KES {collectedTotal.toLocaleString()}</p>
            </div>
            <BanknotesIcon className="w-12 h-12 text-green-200" />
          </div>
          <p className="text-green-100 text-sm mt-2">{totals.paid?.count || 0} paid in full</p>
        </div>

        <div className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl p-6 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-purple-100 text-sm">Waived</p>
              <p className="text-3xl font-bold mt-1">
                KES {(totals.waived?.total || 0).toLocaleString()}
              </p>
            </div>
            <ReceiptRefundIcon className="w-12 h-12 text-purple-200" />
          </div>
          <p className="text-purple-100 text-sm mt-2">{totals.waived?.count || 0} waived</p>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex gap-2">
            <select
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value)
                setCurrentPage(1)
              }}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            >
              <option value="all">All Status</option>
              <option value="pending">Pending</option>
              <option value="partial">Partial</option>
              <option value="paid">Paid</option>
              <option value="waived">Waived</option>
            </select>
            <select
              value={typeFilter}
              onChange={(e) => {
                setTypeFilter(e.target.value)
                setCurrentPage(1)
              }}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            >
              <option value="all">All Types</option>
              <option value="late_return">Late Return</option>
              <option value="lost_book">Lost Book</option>
              <option value="damaged_book">Damaged Book</option>
              <option value="other">Other</option>
            </select>
          </div>
        </div>
      </div>

      {/* Fines Table */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-600"></div>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      User
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Type
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Amount
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Paid
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Date
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {fines.map((fine) => {
                    const fineUser = typeof fine.user === 'object' ? fine.user : null
                    const balance = fine.amount - fine.paidAmount

                    return (
                      <tr key={fine._id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center gap-3">
                            <div className="flex-shrink-0 w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center">
                              <UserIcon className="w-4 h-4 text-gray-400" />
                            </div>
                            <div>
                              <p className="text-sm font-medium text-gray-900">
                                {fineUser
                                  ? `${(fineUser as any).firstName} ${(fineUser as any).lastName}`
                                  : 'Unknown'}
                              </p>
                              <p className="text-xs text-gray-500">{(fineUser as any)?.email}</p>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="text-sm text-gray-900">
                            {getFineTypeLabel(fine.fineType)}
                          </span>
                          <p className="text-xs text-gray-500 truncate max-w-[150px]" title={fine.reason}>
                            {fine.reason}
                          </p>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="text-sm font-medium text-gray-900">
                            {fine.currency} {fine.amount.toLocaleString()}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="text-sm text-gray-700">
                            {fine.currency} {fine.paidAmount.toLocaleString()}
                          </span>
                          {balance > 0 && fine.status !== 'waived' && (
                            <p className="text-xs text-red-600">
                              Balance: {fine.currency} {balance.toLocaleString()}
                            </p>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span
                            className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(
                              fine.status
                            )}`}
                          >
                            {fine.status}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                          {new Date(fine.issuedDate).toLocaleDateString()}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm">
                          {['pending', 'partial'].includes(fine.status) && (
                            <div className="flex items-center justify-end gap-1">
                              <button
                                onClick={() => {
                                  setSelectedFine(fine)
                                  setPaymentAmount((fine.amount - fine.paidAmount).toString())
                                  setShowPayModal(true)
                                }}
                                className="px-2 py-1 text-xs font-medium text-green-600 bg-green-50 rounded hover:bg-green-100"
                              >
                                Pay
                              </button>
                              <button
                                onClick={() => {
                                  setSelectedFine(fine)
                                  setShowWaiveModal(true)
                                }}
                                className="px-2 py-1 text-xs font-medium text-purple-600 bg-purple-50 rounded hover:bg-purple-100"
                              >
                                Waive
                              </button>
                            </div>
                          )}
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>

            {fines.length === 0 && (
              <div className="text-center py-12">
                <CurrencyDollarIcon className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-500">No fines found</p>
              </div>
            )}

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-center gap-2 py-4 border-t">
                <button
                  onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Previous
                </button>
                <span className="text-sm text-gray-500">
                  Page {currentPage} of {totalPages}
                </span>
                <button
                  onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Next
                </button>
              </div>
            )}
          </>
        )}
      </div>

      {/* Pay Fine Modal */}
      <AnimatePresence>
        {showPayModal && selectedFine && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
            onClick={() => {
              setShowPayModal(false)
              resetPaymentForm()
            }}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-white rounded-xl p-6 w-full max-w-md"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold text-gray-900">Record Payment</h2>
                <button
                  onClick={() => {
                    setShowPayModal(false)
                    resetPaymentForm()
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <XMarkIcon className="w-6 h-6" />
                </button>
              </div>

              <div className="space-y-4">
                <div className="p-4 bg-gray-50 rounded-lg">
                  <p className="text-sm text-gray-600">Fine Amount</p>
                  <p className="text-2xl font-bold text-gray-900">
                    {selectedFine.currency} {selectedFine.amount.toLocaleString()}
                  </p>
                  <p className="text-sm text-gray-500">
                    Already paid: {selectedFine.currency} {selectedFine.paidAmount.toLocaleString()}
                  </p>
                  <p className="text-sm font-medium text-red-600">
                    Balance: {selectedFine.currency}{' '}
                    {(selectedFine.amount - selectedFine.paidAmount).toLocaleString()}
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Payment Amount *
                  </label>
                  <input
                    type="number"
                    min="0"
                    max={selectedFine.amount - selectedFine.paidAmount}
                    step="0.01"
                    value={paymentAmount}
                    onChange={(e) => setPaymentAmount(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Payment Method *
                  </label>
                  <select
                    value={paymentMethod}
                    onChange={(e) => setPaymentMethod(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  >
                    <option value="cash">Cash</option>
                    <option value="mpesa">M-Pesa</option>
                    <option value="bank_transfer">Bank Transfer</option>
                    <option value="card">Card</option>
                    <option value="other">Other</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Receipt Number
                  </label>
                  <input
                    type="text"
                    value={receiptNumber}
                    onChange={(e) => setReceiptNumber(e.target.value)}
                    placeholder="Optional"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                  <textarea
                    value={paymentNotes}
                    onChange={(e) => setPaymentNotes(e.target.value)}
                    placeholder="Optional notes..."
                    rows={2}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  />
                </div>

                <div className="flex justify-end gap-3 pt-4 border-t">
                  <Button
                    variant="outline"
                    onClick={() => {
                      setShowPayModal(false)
                      resetPaymentForm()
                    }}
                  >
                    Cancel
                  </Button>
                  <Button onClick={handlePayFine} disabled={!paymentAmount}>
                    Record Payment
                  </Button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Waive Fine Modal */}
      <AnimatePresence>
        {showWaiveModal && selectedFine && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
            onClick={() => {
              setShowWaiveModal(false)
              setWaiveReason('')
              setSelectedFine(null)
            }}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-white rounded-xl p-6 w-full max-w-md"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold text-gray-900">Waive Fine</h2>
                <button
                  onClick={() => {
                    setShowWaiveModal(false)
                    setWaiveReason('')
                    setSelectedFine(null)
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <XMarkIcon className="w-6 h-6" />
                </button>
              </div>

              <div className="space-y-4">
                <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
                  <p className="text-sm text-amber-800">
                    You are about to waive a fine of{' '}
                    <span className="font-bold">
                      {selectedFine.currency}{' '}
                      {(selectedFine.amount - selectedFine.paidAmount).toLocaleString()}
                    </span>
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Reason for Waiver *
                  </label>
                  <textarea
                    value={waiveReason}
                    onChange={(e) => setWaiveReason(e.target.value)}
                    placeholder="Enter reason for waiving this fine..."
                    rows={3}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  />
                </div>

                <div className="flex justify-end gap-3 pt-4 border-t">
                  <Button
                    variant="outline"
                    onClick={() => {
                      setShowWaiveModal(false)
                      setWaiveReason('')
                      setSelectedFine(null)
                    }}
                  >
                    Cancel
                  </Button>
                  <Button onClick={handleWaiveFine} disabled={!waiveReason.trim()}>
                    Waive Fine
                  </Button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Create Fine Modal */}
      <AnimatePresence>
        {showCreateModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
            onClick={() => {
              setShowCreateModal(false)
              resetCreateForm()
            }}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-white rounded-xl p-6 w-full max-w-md"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold text-gray-900">Create Fine</h2>
                <button
                  onClick={() => {
                    setShowCreateModal(false)
                    resetCreateForm()
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <XMarkIcon className="w-6 h-6" />
                </button>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">User *</label>
                  <div className="relative">
                    <input
                      type="text"
                      value={userSearch}
                      onChange={(e) => {
                        setUserSearch(e.target.value)
                        searchUsers(e.target.value)
                      }}
                      placeholder="Search user..."
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    />
                    {userResults.length > 0 && (
                      <div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                        {userResults.map((u) => (
                          <button
                            key={u._id}
                            onClick={() => {
                              setSelectedUser(u)
                              setUserSearch(`${u.firstName} ${u.lastName}`)
                              setUserResults([])
                            }}
                            className="w-full px-4 py-2 text-left hover:bg-gray-50"
                          >
                            <p className="text-sm font-medium text-gray-900">
                              {u.firstName} {u.lastName}
                            </p>
                            <p className="text-xs text-gray-500">{(u as any).email}</p>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                  {selectedUser && (
                    <p className="text-xs text-green-600 mt-1">
                      Selected: {selectedUser.firstName} {selectedUser.lastName}
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Fine Type *</label>
                  <select
                    value={newFineType}
                    onChange={(e) => setNewFineType(e.target.value as FineType)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  >
                    <option value="late_return">Late Return</option>
                    <option value="lost_book">Lost Book</option>
                    <option value="damaged_book">Damaged Book</option>
                    <option value="lost_card">Lost Card</option>
                    <option value="other">Other</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Amount (KES) *</label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={newFineAmount}
                    onChange={(e) => setNewFineAmount(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Reason *</label>
                  <textarea
                    value={newFineReason}
                    onChange={(e) => setNewFineReason(e.target.value)}
                    placeholder="Enter reason for this fine..."
                    rows={3}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  />
                </div>

                <div className="flex justify-end gap-3 pt-4 border-t">
                  <Button
                    variant="outline"
                    onClick={() => {
                      setShowCreateModal(false)
                      resetCreateForm()
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={handleCreateFine}
                    disabled={!selectedUser || !newFineAmount || !newFineReason}
                  >
                    Create Fine
                  </Button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}

export default FinesPage
